import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Chip } from "@nextui-org/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSave } from "@fortawesome/free-solid-svg-icons";
import toast, { Toaster } from "react-hot-toast";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import LoadingPage from "../../components/Loader/LoadingPage";
import UIFormBuilder from "./UIFormBuilder";
import AIFormBuilder from "./AIFormBuilder";
import JsonFormBuilder from "./JsonFormBuilder";
import { getDataWithFilterUrl } from "../../controllers/APIController";

const FormBuilder = ({ metricInfo, isLoadingMetricInfo = false }) => {
  const [searchParams] = useSearchParams();
  const metricIdToCopy = searchParams.get('id');
  const [isCopying, setIsCopying] = useState(false);
  const [copiedMetricInfo, setCopiedMetricInfo] = useState(null);
  const [activeTab, setActiveTab] = useState("Using Builder UI");

  // Handle copying existing metric
  useEffect(() => {
    if (metricIdToCopy) {
      setIsCopying(true);
      console.log("Starting to copy metric with ID:", metricIdToCopy);
      
      // Fetch the existing metric data to copy
      getDataWithFilterUrl("metrics", `filters[id][$eqi]=${metricIdToCopy}`)
        .then((data) => {
          console.log("API response for copying:", data);
          if (data?.data?.[0]) {
            const metricData = data.data[0];
            setCopiedMetricInfo(metricData);
            console.log("Metric data fetched for copying:", metricData);
            toast.success(`Successfully loaded "${metricData.attributes?.Name}" for copying!`);
          } else {
            console.warn("No metric data found for ID:", metricIdToCopy);
            toast.error("Failed to load metric data for copying");
          }
        })
        .catch((error) => {
          console.error("Error fetching metric to copy:", error);
          toast.error("Failed to load metric data for copying");
        })
        .finally(() => {
          setIsCopying(false);
        });
    }
  }, [metricIdToCopy]);

  // Use copied metric info if available, otherwise use the passed metricInfo
  const finalMetricInfo = copiedMetricInfo || metricInfo;
  const isLoading = isLoadingMetricInfo || isCopying;

  const tabs = [
    {
      id: "Using Builder UI",
      label: "Using Builder UI",
      content: <UIFormBuilder metricInfo={finalMetricInfo} isCopying={isCopying} />,
    },
    {
      id: "Using AI",
      label: "Using AI",
      content: <AIFormBuilder metricInfo={finalMetricInfo} isCopying={isCopying} />,
    },
    {
      id: "Using JSON",
      label: "Using JSON",
      content: <JsonFormBuilder metricInfo={finalMetricInfo} isCopying={isCopying} />,
    },
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      <Toaster position="top-right" />
      {/* Page Header */}
      <div className="bg-white border-b border-neutral-100 px-8 py-8 shadow-sm">
        <div className="flex items-center justify-between max-w-full">
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <h1 className="text-2xl font-semibold text-neutral-900">
                  {isCopying ? "Copy metric" : "Create a metric"}
                </h1>
                {isCopying ? (
                  <Chip color="warning" variant="flat" size="md" className="text-sm">
                    Copying
                  </Chip>
                ) : (
                  <Chip color="success" variant="flat" size="md" className="text-sm">
                    Active
                  </Chip>
                )}
              </div>
              <p className="text-base text-neutral-600 max-w-3xl leading-relaxed">
                {isCopying 
                  ? "You are copying an existing metric. The form has been pre-filled with the original data. You can modify any fields before creating the new metric."
                  : "Create a metric and build that would support your ESG Reporting. Choose your preferred method to build your metric schema."
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              color="danger"
              variant="bordered"
              startContent={<FontAwesomeIcon icon={faTimes} className="text-sm" />}
              onClick={() => window.history.back()}
              size="sm"
              className="px-6"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white px-8 font-semibold shadow-lg"
              startContent={<FontAwesomeIcon icon={faSave} className="text-white text-sm" />}
              size="sm"
            >
              Save Draft
            </Button>
          </div>
        </div>
      </div>

      <BreadCrumbsPage>
        <div className="max-w-7xl mx-auto px-8 py-8">
          <LoadingPage isLoading={isLoading} loadingText="Loading form builder...">
            {/* Show copying indicator */}
            {isCopying && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-600"></div>
                  <p className="text-amber-800 font-medium">Loading metric data for copying...</p>
                </div>
              </div>
            )}
            
            {/* Combined Description and Form Sections */}
            <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
              {/* Description Header */}
              <div className="px-8 py-4 border-b border-neutral-200 bg-gradient-to-r from-neutral-50 to-white">
                <h2 className="text-xl font-semibold text-neutral-900 mb-3">Metric Builder Overview</h2>
                <p className="text-base text-neutral-600 leading-relaxed">
                  Choose your preferred method to build your metric schema. You can use the visual builder, AI assistance, or JSON configuration.
                </p>
              </div>
              
              {/* Modern Segmented Button Tabs */}
              <div className="p-8">
                <div className="space-y-6">
                  {/* Builder Options Section */}
                  <div>
                    {/* <h3 className="text-lg font-semibold text-neutral-900 mb-4">Builder Options</h3>
                    <p className="text-sm text-neutral-600 mb-6">Select your preferred editing method</p>
                     */}
                    {/* Segmented Button Group */}
                    <div className="flex gap-2 p-1 bg-neutral-100 rounded-lg w-fit">
                      {tabs.map((tab) => (
                        <Button
                          key={tab.id}
                          variant={activeTab === tab.id ? "solid" : "light"}
                          color={activeTab === tab.id ? "primary" : "default"}
                          className={`px-6 py-3 font-medium transition-all duration-200 ${
                            activeTab === tab.id
                              ? "bg-primary-600 text-white shadow-md"
                              : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-200"
                          }`}
                          onClick={() => setActiveTab(tab.id)}
                        >
                          {tab.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="mt-8">
                    {tabs.find(tab => tab.id === activeTab)?.content}
                  </div>
                </div>
              </div>
            </div>
          </LoadingPage>
        </div>
      </BreadCrumbsPage>
    </div>
  );
};

export default FormBuilder;
